import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerProps,
  Button,
  Form,
  Stack,
  Uploader,
  Message,
  Loader,
  useToaster,
  Schema,
  SelectPicker,
  DatePicker,
  IconButton,
  Divider,
  Checkbox,
  InputPicker,
  Container,
  Radio,
  DOMHelper,
  Whisper,
  Popover,
  PanelGroup,
  Panel,
  TagPicker
} from 'rsuite';
import AvatarIcon from '@rsuite/icons/legacy/Avatar';
import { addUser, getAdminUsers, getManagers } from '@/services/user.service';
import { getGrades } from '@/services/grade.service';
import { getServicelines } from '@/services/serviceline.service';
import { getWfhProfiles } from '@/services/wfhProfile.service';
import { getAccessProfiles } from '@/services/accessProfile.service';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getCurrentUser } from '@/services/auth.service';
import { Icon } from '@rsuite/icons';
import { VscSave } from 'react-icons/vsc';
import { FaRegBuilding, FaRegUser } from 'react-icons/fa';
import { MdOutlineHomeWork } from 'react-icons/md';
import { LuUserCog } from 'react-icons/lu';

const { StringType } = Schema.Types;

const model = Schema.Model({
  firstname: StringType().isRequired('This field is required.'),
  lastname: StringType().isRequired('This field is required.'),
  registrationNumber: StringType().isRequired('This field is required.'),
  Email: StringType()
    .isEmail('Please enter a valid email address.')
    .isRequired('This field is required.'),
  grade: StringType().isRequired('This field is required.'),
  serviceLine: StringType().isRequired('This field is required.')
});

function previewFile(file, callback) {
  const reader = new FileReader();
  reader.onloadend = () => {
    callback(reader.result);
  };
  reader.readAsDataURL(file);
}

const DrawerView = (props: DrawerProps) => {
  const { onClose, ...rest } = props;

  const toaster = useToaster();
  const dispatch = useAppDispatch();
  const currentUser = getCurrentUser();
  const { getHeight } = DOMHelper;

  const loading = useAppSelector((state: any) => state.users.loading);

  // const { users, managers, features, grades, servicelines, wfhProfiles, accessProfiles } =
  //   useAppSelector(state => ({
  //     users: state.users.data,
  //     managers: state.users.managers,
  //     features: state.feature.data,
  //     grades: state.grade.data,
  //     servicelines: state.serviceLine.data,
  //     wfhProfiles: state.wfhProfile.data,
  //     accessProfiles: state.accessProfile.data
  //   }));
  const users = useAppSelector((state: any) => state.users.data)?.map(item => {
    return { value: item._id, label: item.firstname + ' ' + item.lastname };
  });
  const managers = useAppSelector((state: any) => state.users.managers)?.map(item => ({
    value: item._id,
    label: `${item.firstname} ${item.lastname}`
  }));

  const features = useAppSelector((state: any) => state.feature.data);

  const grades = useAppSelector((state: any) => state.grade.data)?.map(item => {
    return { value: item._id, label: item.grade_name };
  });

  const servicelines = useAppSelector((state: any) => state.serviceLine.data)?.map(item => {
    return { value: item._id, label: item.serviceLine, SubServiceLines: item.SubServiceLines };
  });

  const wfhProfiles = useAppSelector((state: any) => state.wfhProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const [userData, setUserData] = useState<any>({});
  const [subservicelines, setSubservicelines] = useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [modules, setModules] = useState<any[]>([]);
  const [fileInfo, setFileInfo] = React.useState(null);
  const { t } = useTranslation();

  // Reset form function
  const resetForm = () => {
    setUserData({});
    setFileInfo(null);
    setSubservicelines([]);
    setUploading(false);
  };

  useEffect(() => {
    dispatch(getAdminUsers());
    dispatch(getManagers());
    dispatch(getGrades());
    dispatch(getServicelines());
    dispatch(getWfhProfiles());
    dispatch(getAccessProfiles());
  }, []);
  useEffect(() => {
    const featuresArray: any = [
      {
        value: 'organizationSettings',
        label: 'Members & Organization settings',
        name: 'Members & Organization settings'
      }
    ];

    const addedFeatures = new Set();

    features?.forEach((project: any) => {
      const featureName = project.name?.toLowerCase();

      if (featureName === 'on site' && !addedFeatures.has('flexOffice')) {
        featuresArray.push({ label: 'Flex Office', value: 'flexOffice' });
        addedFeatures.add('flexOffice');
      }
      if (featureName === 'wfh' && !addedFeatures.has('remoteWork')) {
        featuresArray.push({ label: 'Remote Work', value: 'remoteWork' });
        addedFeatures.add('remoteWork');
      }
      if (featureName === 'leave' && !addedFeatures.has('Leave')) {
        featuresArray.push({ label: 'Leave', value: 'leave' });
        addedFeatures.add('Leave');
      }
      if (featureName === 'travel' && !addedFeatures.has('travel')) {
        featuresArray.push({ label: 'Business Travel', value: 'travel' });
        addedFeatures.add('travel');
      }
    });

    if (!addedFeatures.has('analytics')) {
      featuresArray.push({ label: 'Analytics', value: 'analytics' });
      addedFeatures.add('analytics');
    }

    setModules(featuresArray);
  }, [features]);

  useEffect(() => {
    if (userData?.serviceLine) {
      const subservicelines = servicelines.find(
        item => item.value == userData?.serviceLine
      )?.SubServiceLines;
      const values = subservicelines?.map(item => {
        return { value: item, label: item };
      });
      setSubservicelines(values || []);
    } else {
      setSubservicelines([]);
    }
  }, [userData?.serviceLine]);

  const profiles = [
    { label: t('Facility Administrator'), value: 'facility' },
    { label: t('DAF Administrator'), value: 'daf' },
    { label: t('Super Administrator'), value: 'super' }
  ];

  const changeUser = (field, value) => {
    setUserData(prevData => ({ ...prevData, [field]: value }));
  };

  const speaker = (
    <Popover
      title={
        wfhProfiles.find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
          ?.label
      }
    >
      <p>{t('Authorized days:')} </p>
      {wfhProfiles
        .find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
        ?.authorizedWfhDays?.map((day, index) => (
          <p key={index}>{day}</p>
        ))}
    </Popover>
  );

  const renderMenuItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || 'WFH Profile'}>
        <p>{t('Authorized days:')}</p>
        {item.authorizedWfhDays?.length > 0 ? (
          item.authorizedWfhDays.map((day, index) => <p key={index}>{day}</p>)
        ) : (
          <p>{t('No authorized days')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  const speakerAccess = (
    <Popover
      title={
        accessProfiles.find(item => item.value == (userData.Group?._id || userData.Group))?.label
      }
    >
      <p>Zones: </p>
      {accessProfiles
        .find(item => item.value == (userData.Group?._id || userData.Group))
        ?.zones?.map((zone, index) => (
          <p key={index}>{zone.name}</p>
        ))}
    </Popover>
  );

  const renderAccessItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || t('Access Profile')}>
        <p>Zones:</p>
        {item.zones?.length > 0 ? (
          item.zones.map((zone, index) => <p key={index}>{zone.name}</p>)
        ) : (
          <p>{t('No accessible zones')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  const handleSave = async () => {
    let hasErrors = false;

    const errors = model.check(userData);
    Object.entries(errors).forEach(([field, error]) => {
      if (error.hasError) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Fill required fields')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    const formData = new FormData();

    const appendIfValid = (key: string, value: any) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(v => formData.append(`${key}[]`, v));
      } else {
        formData.append(key, value);
      }
    };
    Object.keys(userData).forEach(key => {
      appendIfValid(key, userData[key]);
    });

    try {
      await dispatch(addUser(formData)); // No .unwrap() needed
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Added successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      resetForm();
      onClose();
    } catch (e) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(e.message || 'Failed to add user')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  const handleClose = () => {
    resetForm(); // Reset form when closing the drawer
    onClose(); // Call the original onClose prop
  };

  return (
    <Drawer backdrop="static" size="lg" placement="right" onClose={handleClose} {...rest}>
      <Form fluid model={model}>
        <Drawer.Header>
          <Drawer.Title>{t('Add a new member')}</Drawer.Title>
          <Drawer.Actions>
            <IconButton
              loading={loading}
              onClick={handleSave}
              appearance="primary"
              icon={<Icon as={VscSave} />}
            >
              {t('Save')}
            </IconButton>
            <Button onClick={handleClose} appearance="subtle">
              {t('Cancel')}
            </Button>
          </Drawer.Actions>
        </Drawer.Header>

        <Drawer.Body style={{ height: Math.max(getHeight(window) - 100, 400), overflow: 'auto' }}>
          <Container>
            <Uploader
              fileListVisible={false}
              listType="picture"
              action={`${process.env.REACT_APP_API_URL}User/uploadFiles/`}
              onUpload={file => {
                setUploading(true);
                previewFile(file.blobFile, value => {
                  setFileInfo(value);
                });
              }}
              onSuccess={(response, file) => {
                setUploading(false);
                toaster.push(
                  <Message type="success" showIcon>
                    {t('Uploaded successfully')}
                  </Message>
                );
              }}
              onError={() => {
                setFileInfo(null);
                setUploading(false);
                toaster.push(
                  <Message type="error" showIcon>
                    {t('Upload failed')}
                  </Message>
                );
              }}
            >
              <button style={{ width: 100, height: 100 }}>
                {uploading && <Loader backdrop center />}
                {fileInfo ? (
                  <img src={fileInfo} width="100%" height="100%" />
                ) : (
                  <AvatarIcon style={{ fontSize: 80 }} />
                )}
              </button>
            </Uploader>

            <PanelGroup>
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 50 }}>
                    <FaRegUser style={{ fontSize: '16px' }} />
                    <span>{t('User Information')}</span>
                  </div>
                }
              >
                <Stack spacing={20} wrap style={{ marginBottom: 20, marginTop: 20 }}>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('First Name')} <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      name="firstname"
                      style={{ width: 200 }}
                      value={userData?.firstname || ''}
                      onChange={val => {
                        changeUser('firstname', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('Last Name')} <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      name="lastname"
                      style={{ width: 200 }}
                      value={userData?.lastname || ''}
                      onChange={val => {
                        changeUser('lastname', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('Registration Number')}{' '}
                      <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      name="registrationNumber"
                      style={{ width: 200 }}
                      value={userData?.registrationNumber || ''}
                      onChange={val => {
                        changeUser('registrationNumber', val);
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.ControlLabel>
                      Email <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      name="Email"
                      type="email"
                      style={{ width: 200 }}
                      value={userData?.Email}
                      onChange={val => {
                        changeUser('Email', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Phone')}</Form.ControlLabel>
                    <Form.Control
                      name="phone"
                      type="number"
                      style={{ width: 200 }}
                      value={userData?.telephone || ''}
                      onChange={val => {
                        changeUser('telephone', val);
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.ControlLabel>
                      Grade <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={SelectPicker}
                      name="grade"
                      data={grades}
                      style={{ width: 200 }}
                      value={userData?.grade?._id || userData?.grade || ''}
                      onChange={val => {
                        changeUser('grade', val);
                      }}
                      placement="auto"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>
                      {t('Department')} <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
                    </Form.ControlLabel>
                    <Form.Control
                      accepter={SelectPicker}
                      name="serviceLine"
                      placement="auto"
                      data={servicelines}
                      style={{ width: 200 }}
                      value={userData?.serviceLine?._id || userData?.serviceLine || ''}
                      onChange={val => {
                        changeUser('serviceLine', val);
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.ControlLabel>{t('Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={managers}
                      style={{ width: 200 }}
                      value={userData?.manager?._id || userData?.manager || ''}
                      onChange={val => {
                        changeUser('manager', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Second Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={managers}
                      style={{ width: 200 }}
                      value={userData?.secondValidator?._id || userData?.secondValidator || ''}
                      onChange={val => {
                        changeUser('secondValidator', val);
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.ControlLabel>{t('Hire date')}</Form.ControlLabel>
                    <DatePicker
                      format="dd-MM-yyyy"
                      placement="auto"
                      value={userData.hire_date ? new Date(userData.hire_date) : null}
                      style={{ width: 200 }}
                      onChange={val => {
                        changeUser('hire_date', val);
                      }}
                    />
                  </Form.Group>
                </Stack>
              </Panel>

              {features?.find(item => item.name?.toLowerCase() == 'wfh') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MdOutlineHomeWork style={{ fontSize: '16px' }} />
                      <span>{t('Remote working')}</span>
                    </div>
                  }
                >
                  <Stack spacing={20}>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="control-id-hover"
                      speaker={speaker}
                    >
                      <Form.Group>
                        <Form.ControlLabel>{t('Home Office Profile')}</Form.ControlLabel>
                        <SelectPicker
                          placement="auto"
                          data={wfhProfiles}
                          style={{ width: 200 }}
                          value={userData?.wfhProfile?._id || userData?.wfhProfile || ''}
                          onChange={val => {
                            changeUser('wfhProfile', val);
                          }}
                          renderMenuItem={(label, item) => renderMenuItem(label, item)}
                        />
                      </Form.Group>
                    </Whisper>
                    <Form.Group>
                      <Form.ControlLabel>{t('WFH Validator')}</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={managers}
                        style={{ width: 200 }}
                        value={userData?.WFH_validator?._id || userData?.WFH_validator}
                        onChange={val => {
                          changeUser('WFH_validator', val);
                        }}
                      />
                    </Form.Group>
                  </Stack>
                </Panel>
              )}
              {features?.find(item => item.name?.toLowerCase() == 'on site') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaRegBuilding style={{ fontSize: '16px' }} />
                      <span>{t('Flex office')}</span>
                    </div>
                  }
                >
                  <Whisper
                    placement="top"
                    trigger="hover"
                    controlId="control-id-hover"
                    speaker={speakerAccess}
                  >
                    <Form.Group>
                      <Form.ControlLabel>{t('Access profile')}</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={accessProfiles}
                        style={{ width: 200 }}
                        value={userData?.Group?._id || userData?.Group || ''}
                        onChange={val => {
                          changeUser('Group', val);
                        }}
                        renderMenuItem={(label, item) => renderAccessItem(label, item)}
                      />
                    </Form.Group>
                  </Whisper>
                </Panel>
              )}

              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LuUserCog style={{ fontSize: '16px' }} />
                    <span>{t('Roles')}</span>
                  </div>
                }
              >
                <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                  <Stack spacing={10}>
                    <Radio
                      value="user"
                      checked={userData.role !== 'trainee'}
                      onChange={() => {
                        changeUser('role', 'user');
                      }}
                    >
                      Employee
                    </Radio>
                    <Radio
                      value="trainee"
                      checked={userData.role === 'trainee'}
                      onChange={() => {
                        changeUser('role', 'trainee');
                        changeUser('isAdmin', false);
                        changeUser('isManager', false);
                        changeUser('isValidator', false);
                        changeUser('admin', null);
                      }}
                    >
                      {t('Trainee')}
                    </Radio>
                  </Stack>

                  <Checkbox
                    onChange={() => {
                      changeUser('isManager', !userData.isManager);
                    }}
                    checked={userData.isManager || false}
                    disabled={userData.role === 'trainee'}
                  >
                    {t('Is Manager')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isValidator', !userData.isValidator);
                    }}
                    checked={userData.isValidator || false}
                    disabled={userData.role === 'trainee'}
                  >
                    {t('Is Validator')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isGFSF', !userData.isGFSF);
                    }}
                    checked={userData.isGFSF || false}
                    disabled={userData.role === 'trainee'}
                  >
                    {t('Is GFSF')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isSST', !userData.isSST);
                    }}
                    checked={userData.isSST || false}
                    disabled={userData.role === 'trainee'}
                  >
                    {t('Is SST')}
                  </Checkbox>
                  {currentUser?.admin === 'super' && (
                    <>
                      <Checkbox
                        onChange={() => {
                          if (userData.isAdmin) {
                            changeUser('admin', null);
                          }
                          changeUser('isAdmin', !userData.isAdmin);
                        }}
                        checked={userData.isAdmin || false}
                        disabled={userData.role === 'trainee'}
                      >
                        {t('Administrator')}
                      </Checkbox>
                      <InputPicker
                        placement="auto"
                        data={profiles}
                        style={{ width: 200 }}
                        disabled={!userData.isAdmin}
                        value={userData.admin || null}
                        onChange={e => {
                          changeUser('admin', e);
                        }}
                      />
                    </>
                  )}
                </Stack>
                {userData.isAdmin && (
                  <Form.Group>
                    <Form.ControlLabel>{t('Module Access')}</Form.ControlLabel>
                    <TagPicker
                      placement="auto"
                      data={modules}
                      value={userData.moduleAccess || []}
                      onChange={val => {
                        changeUser('moduleAccess', val);
                      }}
                      style={{ width: 300 }}
                      placeholder={t('Select modules…')}
                    />
                  </Form.Group>
                )}
              </Panel>
            </PanelGroup>
          </Container>
        </Drawer.Body>
      </Form>
    </Drawer>
  );
};

export default DrawerView;
